/**
 * Golem fork (FT-1010): CHRONICLES — the town's whole story as one stream.
 *
 * Three surfaces merged into one (user decision, 2026-08-20): the town chat,
 * the live chronicle, and the town records. The stream is the PERSISTENT town
 * log (FT-963/987 — one permanent room per town, a game is a filter over it),
 * with GAME EVENTS as rows in that same log beside the talk, so the story
 * survives every reload and reaches every client from one store.
 *
 * This file owns two things and no rendering:
 *
 *   1. THE EVENT ENVELOPE — how a game event rides a stored chat row.
 *   2. SECTIONING — cutting the one stream into per-game chapters with the
 *      between-games talk standing between them.
 *
 * ── WHY AN ENVELOPE INSIDE `kind: "system"`, NOT A NEW ROW KIND ────────────
 * The lane brief suggested `kind: "event"`. The relay rules it out without
 * being touched: server/chat.js accepts exactly {say, whisper, system} and
 * refuses anything else, and the relay is hand-deployed — this design must
 * not need it changed. What the relay DOES already give `system` is exactly
 * what events need: host-only authorship (a frame from anyone else is
 * refused, so N clients can never write N duplicate rows), store-accepted
 * delivery, broadcast to the whole town. So an event IS a system row, and
 * the event's machine half rides INSIDE the body:
 *
 *   body = "EV1" + JSON.stringify({ t, text, ...detail })
 *
 *   t      the event type — see EVENTS below
 *   text   the human sentence, ALWAYS present, so any surface that does not
 *          know the envelope (an older build, a raw DB read) still has a
 *          renderable line by stripping the prefix — and the envelope can be
 *          dropped entirely someday without losing the story.
 *
 * A system row whose body does NOT start with the prefix is a plain
 * announcement (every pre-FT-1010 row: "A game begins.", "Night 1 falls.")
 * and renders as one. Decode never throws; it answers null and the caller
 * falls back to the raw body.
 */

/** The envelope prefix. Versioned so a future shape can coexist in one log. */
export const EV_PREFIX = "EV1";

/**
 * The event vocabulary — what the host's client writes down as it happens.
 *
 *   start       the deal — a game begins (gameId is born with it)
 *   end         the storyteller named the winner
 *   phase       day/night turned; detail: { night: bool, day: number }
 *   death       a shroud placed;  detail: { name, seat }
 *   revive      a shroud lifted;  detail: { name, seat }
 *   nomination  a vote concluded; detail: { nominator, nominee, type,
 *               votes: number, majority: number, carried: bool,
 *               voters: [names], ghosts: [names], nays: [names] }.
 *               FT-1019: `voters` is WHO RAISED HANDS at the conclusion —
 *               recorded once, when the vote is recorded, never per-hand —
 *               and `ghosts` is the subset who were dead when they did (a
 *               spent ghost vote). Rows written before FT-1019 carry
 *               neither key and render tally-only.
 *               FT-1310: `nays` is the ELIGIBLE seats whose hand stayed
 *               down (a voteless seat sits on neither list — it had no
 *               hand to keep down; socket.js records the set at the
 *               conclusion, Vote.vue's own eligibility rule). A row from
 *               before this key renders its Yes line only.
 *   execution   a seat marked for execution; detail: { name, seat }.
 *               "Marked", not "died" — the storyteller decides what a
 *               majority means, and the record must not invent an outcome
 *               (the same honesty rule golem/chronicle.js stated).
 *   unmark      the mark lifted DELIBERATELY (the storyteller's own clear,
 *               during the day); detail: { name, seat }. The night falling
 *               also clears the mark, but that is the day expiring, not
 *               news — socket.js writes no row for it.
 */
export const EVENTS = [
  "start",
  "end",
  "phase",
  "death",
  "revive",
  "nomination",
  "execution",
  "unmark",
  // FT-1037: the town-session boundary — the storyteller opened the town.
  // Current mode anchors on the LAST of these (openAnchorSeq below).
  "open",
  // FT-1037: a BOARD PORTRAIT — the ring as data, not pixels; detail:
  // { moment: "start" | "day1" | "end", seats: [{name, role, dead,
  // traveler}] }. FT-1057 retimed the opening capture to the DEAL — the
  // board as the game begins, the storyteller's true view — with "day1"
  // kept as the legacy moment older logs already hold. Captured early but
  // POSTED at game end: an opening row broadcast mid-game would hand every
  // player the full grimoire (roles ride the body, and system rows reach
  // everyone). Until then the host alone sees it, rendered from the
  // host-local stash below (peekOpeningBoard), never from the wire.
  "board",
  // FT-1101: A NIGHT'S ACTIONS AS ONE BLOCK; detail: { day, lines: [{ seat,
  // name, roleName, chose: [names], said, learned }], private? }.
  //
  // IT HAS TWO LIVES, and they are the FT-1057 board's exactly:
  //
  //   DURING PLAY it is never a log row at all. Each client builds its own
  //   block SYNTHETICALLY from data it already holds and nobody else's — the
  //   storyteller from night.entries (host-local, never broadcast), a player
  //   from night/myEntries (their own seat's rows, delivered per-seat on the
  //   direct lane by FT-1005 and projected on the way in). Those blocks carry
  //   `private: true` and cannot reach anyone else even in principle: no
  //   frame is sent, so there is nothing on the wire to intercept.
  //
  //   AT GAME END the host publishes one real system row per night, and the
  //   night joins the rest of the finished game's record — public, like the
  //   revealed grimoire beside it (user call: "then when the game ends it
  //   becomes visible to everyone"). App.vue's onGameRecorded is the moment,
  //   beside the board portraits it already publishes there.
  //
  // What NEVER rides either life is the storyteller's lie mark: what a seat
  // was TOLD is the record, and whether the storyteller knew it was false
  // stays theirs. See nightLog's chronicleLineOf.
  "nights",
];

/** Event → stored body. `text` is required; detail keys ride beside it. */
export function encodeEvent(event) {
  return EV_PREFIX + JSON.stringify(event);
}

/**
 * Stored body → event, or null for a plain announcement. Defensive on every
 * edge: a truncated body, hand-typed JSON, an unknown `t` — all null, all
 * rendered as the plain line they still carry.
 */
export function decodeEvent(body) {
  if (typeof body !== "string" || !body.startsWith(EV_PREFIX + "{")) {
    return null;
  }
  try {
    const event = JSON.parse(body.slice(EV_PREFIX.length));
    if (!event || typeof event !== "object") return null;
    if (!EVENTS.includes(event.t)) return null;
    if (typeof event.text !== "string" || !event.text) return null;
    return event;
  } catch (e) {
    return null;
  }
}

/** The human line for ANY system row — decoded text, or the body as it is. */
export function eventTextOf(row) {
  const event = decodeEvent(row.body);
  return event ? event.text : row.body;
}

/** The stream filters. "Talk" is people; "Gallows" is the nomination arc
 *  (FT-1019, user call — the vote-history door's icon lives on as its
 *  filter); "Events" is everything else the town does. FT-1037b (user call):
 *  "nights" is THIS VIEWER'S OWN night learnings — the retired night
 *  drawer's history, now a toggle here. It is a viewer-local projection
 *  (night/myEntries), not town-log rows, so under it the surface renders
 *  the nights view instead of the stream.
 *
 *  FT-1274 CORRECTED THAT LAST SENTENCE. "nights" is a filter over the
 *  STREAM now, exactly like the other four — see inFilter below. */
export const FILTERS = ["all", "talk", "gallows", "events", "nights"];

/** The event types that belong to the gallows — the nomination arc. */
const GALLOWS_T = new Set(["nomination", "execution", "unmark"]);

/** Does a row survive the talk/events filter? System rows ARE the events. */
export function inFilter(row, filter) {
  // FT-1274 (user: the storyteller has no night-actions filter): the moon
  // cell is a FILTER OVER THE STREAM now, and the same one for every reader.
  //
  // It used to be a door to a second surface — ChroniclesNights, which held a
  // player's own rows and worded them its own way ("Fortune Teller / You chose
  // …") while the stream's night block said the same event differently ("You
  // (Fortune Teller) chose …"). One event, two renderers, two sentences. The
  // fix for that is one sentence built once (golem/nightLog's chronicleLineOf)
  // and rendered in one place, so the second surface has nothing left to be:
  // the cell now narrows the stream to the night rows already in it.
  //
  // A player's night rows are synthetic and viewer-local (their own seat's,
  // never anyone else's) and the storyteller's are host-local. So this filter
  // shows each reader exactly what they were already holding — it decides
  // WHICH OF YOUR OWN ROWS you are looking at, never who may hold one.
  if (filter === "nights") {
    if (row.kind !== "system") return false;
    const ev = decodeEvent(row.body);
    return !!ev && ev.t === "nights";
  }
  if (filter === "talk") return row.kind !== "system";
  if (filter === "gallows") {
    if (row.kind !== "system") return false;
    const ev = decodeEvent(row.body);
    return !!ev && GALLOWS_T.has(ev.t);
  }
  if (filter === "events") return row.kind === "system";
  return true;
}

/**
 * Cut the stream into SECTIONS: one per game, in the order the games were
 * played, with between-games rows standing between them as their own
 * uncollapsible runs. Order in = order out (the caller hands rows ascending
 * by seq; a game slice keeps the town's numbering, so a section is simply a
 * consecutive run of one gameId).
 *
 * Returns [{ key, gameId, ordinal, rows }]:
 *   key      stable across rebuilds (Vue :key + the collapse map's key)
 *   gameId   null for a between-games run
 *   ordinal  1-based game number in this town's story; null between games
 *
 * A row with an unexpected out-of-run gameId (should not happen — a game's
 * rows are written while it is live, so they are contiguous) starts a new
 * run rather than being dropped: the story must hold every row somewhere.
 */
export function sectionize(rows) {
  const sections = [];
  let current = null;
  let ordinal = 0;
  (rows || []).forEach((row) => {
    const gameId = row.gameId || null;
    if (!current || current.gameId !== gameId) {
      if (gameId) ordinal += 1;
      current = {
        key: gameId ? gameId : "between:" + (row.seq || sections.length),
        gameId,
        ordinal: gameId ? ordinal : null,
        rows: [],
      };
      sections.push(current);
    }
    current.rows.push(row);
  });
  return sections;
}

/**
 * The games the picker offers, newest first, derived from the rows in hand —
 * the same derivation everywhere, so the picker and the sections can never
 * disagree about which games exist.
 */
export function gamesOf(rows) {
  const seen = new Map();
  (rows || []).forEach((row) => {
    if (row.gameId && !seen.has(row.gameId)) {
      seen.set(row.gameId, {
        gameId: row.gameId,
        ordinal: seen.size + 1,
        startedAt: row.createdAt || null,
      });
    }
  });
  return [...seen.values()].reverse();
}

/** "20 Aug" for a section header, or "" when the clock is unreadable. */
/** FT-1020 (user call): a game is LABELLED by when it began — "20 Aug 21:15" —
 *  not by an ordinal. */
export function startLabelOf(createdAt) {
  const at = Date.parse(createdAt);
  if (!Number.isFinite(at)) return "";
  const d = new Date(at);
  const hm =
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0");
  return dayOf(createdAt) + " · " + hm;
}

/**
 * FT-1019: THE GALLOWS THREAD — the strand a nomination row unfolds into.
 *
 * A concluded nomination is one beat of a longer arc: hands went up, the
 * storyteller may have marked the nominee for execution, may have lifted the
 * mark again, and the nominee may then have died. The roster travels INSIDE
 * the nomination's own envelope (voters/ghosts); the mark, the unmark and the
 * outcome are their own rows in the same stream, written as they happened.
 * This walks FORWARD from the nomination row and gathers the rows that belong
 * to ITS arc:
 *
 *   · same game only — a thread never crosses a chapter
 *   · about the NOMINEE by name — another seat's mark is another arc
 *   · until the day turns (a `phase` row) — the gallows is a daytime
 *     machine and the night falling ends every standing arc — or until the
 *     same nominee is nominated AGAIN (a fresh arc supersedes)
 *
 * Returns { mark, unmark, death } — each the matched ROW or null. Honest by
 * construction: it reports rows that were actually written, never inferring
 * an outcome from a majority (the record's own rule).
 */
export function gallowsThreadOf(rows, nominationRow) {
  const out = { mark: null, unmark: null, death: null };
  const nom = decodeEvent(nominationRow.body);
  if (!nom || nom.t !== "nomination") return out;
  const from = (rows || []).findIndex((r) => r.seq === nominationRow.seq);
  if (from < 0) return out;
  for (let i = from + 1; i < rows.length; i++) {
    const row = rows[i];
    if (row.gameId !== nominationRow.gameId) break;
    if (row.kind !== "system") continue;
    const ev = decodeEvent(row.body);
    if (!ev) continue;
    if (ev.t === "phase") break;
    if (ev.t === "nomination" && ev.nominee === nom.nominee) break;
    if (ev.name !== nom.nominee) continue;
    if (ev.t === "execution" && !out.mark) out.mark = row;
    else if (ev.t === "unmark" && out.mark && !out.unmark) out.unmark = row;
    else if (ev.t === "death" && !out.death) out.death = row;
  }
  return out;
}

/**
 * FT-1019: a RECORDED game (the games API's DTO) → the id its rows carry in
 * the town log, or null when the bridge cannot be built. The log's gameId is
 * minted from the town and the deal instant (golem/chat's gameIdFor); the
 * recorded game's `startedAt` IS that same stashed instant, posted with the
 * record — so the two sides meet on `g-<town>-<ms>` without either being
 * taught about the other. A record with no startedAt (never dealt through
 * this client, or a pre-FT-965 row) has no bridge and answers null.
 */
export function logGameIdOf(townId, startedAt) {
  if (!townId || !startedAt) return null;
  const at = Date.parse(startedAt);
  if (!Number.isFinite(at)) return null;
  return `g-${townId}-${at}`;
}

/** FT-1032 (user call): how long each phase RAN, keyed by the phase-turn
 *  row that ENDED it — "Night 1 falls" carries the length of the day (or
 *  setup) it closed. Computed from the rows' own timestamps; only phase
 *  turns and the game start anchor the clock. */
export function phaseDurations(rows) {
  const out = {};
  let anchorAt = null;
  for (const row of rows) {
    if (row.kind !== "system") continue;
    const ev = decodeEvent(row.body);
    if (!ev) continue;
    const at = Date.parse(row.createdAt);
    if (ev.t === "start") {
      anchorAt = at;
      continue;
    }
    if (ev.t !== "phase") continue;
    if (anchorAt != null && Number.isFinite(at))
      out[row.id] = Math.max(0, Math.round((at - anchorAt) / 1000));
    anchorAt = at;
  }
  return out;
}

/**
 * FT-1037: THE CURRENT ANCHOR — the seq of the LAST `open` row in the log,
 * or 0 when the town has never written one. Current mode shows rows at or
 * after this seq; History reads per game. Derived from the log itself so
 * every client — host, player, a reload mid-game — computes the same
 * boundary from the same rows, with no per-browser state to drift.
 */
export function openAnchorSeq(rows) {
  let anchor = 0;
  (rows || []).forEach((row) => {
    if (row.kind !== "system") return;
    const ev = decodeEvent(row.body);
    if (ev && ev.t === "open") anchor = row.seq;
  });
  return anchor;
}

/**
 * FT-1037: the ring as DATA — one compact seat per chair, in seat order.
 * Names capped so a 20-seat ring stays well under the store's BODY_MAX.
 */
export function boardRingOf(players) {
  return (players || []).map((player, i) => ({
    name: ((player && player.name) || `Seat ${i + 1}`).slice(0, 32),
    role: (player && player.role && player.role.id) || "",
    dead: !!(player && player.isDead),
    traveler: !!(player && player.role && player.role.team === "traveler"),
  }));
}

/**
 * FT-1037: a game's portraits, decoded — { start, day1, end }, each the
 * board EVENT or null. `start` is FT-1057's opening board (the deal);
 * `day1` is the legacy moment older logs hold. The first start/day1 wins
 * and the last end wins, so a duplicate row (a re-recorded end) can never
 * split the set.
 */
export function boardsOf(rows, gameId) {
  const out = { start: null, day1: null, end: null };
  (rows || []).forEach((row) => {
    if (row.gameId !== gameId || row.kind !== "system") return;
    const ev = decodeEvent(row.body);
    if (!ev || ev.t !== "board" || !Array.isArray(ev.seats)) return;
    if (ev.moment === "start" && !out.start) out.start = ev;
    else if (ev.moment === "day1" && !out.day1) out.day1 = ev;
    else if (ev.moment === "end") out.end = ev;
  });
  return out;
}

/**
 * FT-1375: HOW EACH SEAT DIED, inferred from the game's own log rows — the
 * record's roster says WHEN a seat died (deathDay/deathPhase, FT-1163) but
 * deliberately never WHY ("the app records THAT a seat died, never why" —
 * socket.js's death row). The log knows more than the record does, honestly:
 *
 *   executed   a `death` row landing while an execution MARK stood on that
 *              same seat/name — the same arc gallowsThreadOf walks, read
 *              from the mark's side. The mark clears on `unmark` and on any
 *              phase turn (the day expiring), exactly as the town's does.
 *   demon      a seat the RECORD says died at night N, where the game's
 *              published night log (`nights` row for day N — public only
 *              after game end, FT-1101) shows a line whose seat holds a
 *              demon (per the record's own roleType) choosing that player
 *              by name. The demon's recorded pick + the death the same
 *              night is the record's own claim, not a guess.
 *
 * Anything else stays UNATTRIBUTED — no "probably", no default. A revive
 * clears the seat's inference (a later death re-earns or doesn't), and a
 * later unmarked death overwrites an earlier "executed" (last death wins,
 * matching the record's own final-state discipline).
 *
 * @param rows    the town's log rows (ascending)
 * @param gameId  the log-side game id (logGameIdOf)
 * @param seats   the RECORD's seats (seatNo 1-based, roleType, playerName,
 *                deathDay, deathPhase, survived)
 * @returns a plain object: seatNo -> "executed" | "demon" (absent = unknown)
 */
export function fatesOf(rows, gameId, seats) {
  const out = {};
  const nightsByDay = new Map();
  let mark = null; // { name, seat } — the standing execution mark
  (rows || []).forEach((row) => {
    if (!gameId || row.gameId !== gameId || row.kind !== "system") return;
    const ev = decodeEvent(row.body);
    if (!ev) return;
    if (ev.t === "phase") {
      mark = null; // the day expired; the night falling clears the mark
    } else if (ev.t === "execution") {
      mark = { name: ev.name, seat: ev.seat };
    } else if (ev.t === "unmark") {
      if (mark && mark.name === ev.name) mark = null;
    } else if (ev.t === "nights") {
      if (typeof ev.day === "number" && Array.isArray(ev.lines)) {
        nightsByDay.set(ev.day, ev.lines);
      }
    } else if (ev.t === "revive") {
      if (typeof ev.seat === "number") delete out[ev.seat + 1];
    } else if (ev.t === "death") {
      if (typeof ev.seat !== "number") return;
      const seatNo = ev.seat + 1;
      if (mark && (mark.seat === ev.seat || mark.name === ev.name)) {
        out[seatNo] = "executed";
      } else {
        delete out[seatNo]; // last death wins; this one is unattributed so far
      }
    }
  });
  // The demon pass — over the RECORD's own night deaths, so a shroud dropped
  // after dawn still matches the moment the record actually stored.
  const demonSeats = new Set(
    (seats || [])
      .filter((s) => s && s.roleType === "demon")
      .map((s) => s.seatNo),
  );
  (seats || []).forEach((s) => {
    if (!s || s.survived || out[s.seatNo]) return;
    if (s.deathPhase !== "night" || typeof s.deathDay !== "number") return;
    const lines = nightsByDay.get(s.deathDay);
    if (!lines) return;
    const chosen = lines.some(
      (l) =>
        l &&
        typeof l.seat === "number" &&
        demonSeats.has(l.seat + 1) &&
        Array.isArray(l.chose) &&
        l.chose.includes(s.playerName),
    );
    if (chosen) out[s.seatNo] = "demon";
  });
  return out;
}

/** FT-1037: a game's winner from its own `end` row — for a game the log
 *  holds but the records API never got. */
export function winnerOf(rows, gameId) {
  let winner = null;
  (rows || []).forEach((row) => {
    if (row.gameId !== gameId || row.kind !== "system") return;
    const ev = decodeEvent(row.body);
    if (ev && ev.t === "end" && (ev.winner === "good" || ev.winner === "evil"))
      winner = ev.winner;
  });
  return winner;
}

/*
 * FT-1037: THE TOWN-SESSION STASH — is this connect a fresh OPENING, or the
 * same sitting resumed (a reload, a relay blip)? localStorage `golem.townOpen`
 * holds {townId: {at, seen}}. A host whose `seen` is within OPEN_STALE_MS is
 * resuming and writes no new open row; older (or absent) means the town was
 * shut and this connect opens it. socket.js bumps `seen` on a heartbeat while
 * hosting, so "recently seen" literally means "the town was recently open".
 */
const OPEN_KEY = "golem.townOpen";
const OPEN_STALE_MS = 45 * 60 * 1000;

function readOpenStash() {
  try {
    return JSON.parse(localStorage.getItem(OPEN_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeOpenStash(stash) {
  try {
    localStorage.setItem(OPEN_KEY, JSON.stringify(stash));
  } catch (e) {
    // storage denied — the host just re-opens each visit, honestly
  }
}

/** Host connects: true = a FRESH opening (write the open row), false = the
 *  same sitting resumed. Stamps/bumps the stash either way. */
export function beginTownSession(townId) {
  if (!townId) return false;
  const stash = readOpenStash();
  const now = Date.now();
  const prior = stash[townId];
  const fresh = !prior || !prior.seen || now - prior.seen > OPEN_STALE_MS;
  stash[townId] = { at: fresh ? now : prior.at, seen: now };
  writeOpenStash(stash);
  return fresh;
}

/** The hosting heartbeat — keeps `seen` current while the town is open. */
export function touchTownSession(townId) {
  if (!townId) return;
  const stash = readOpenStash();
  if (!stash[townId]) return;
  stash[townId].seen = Date.now();
  writeOpenStash(stash);
}

/*
 * FT-1057: THE OPENING BOARD STASH — the ring captured AT THE DEAL (FT-1037
 * captured it as Day 1 broke; FT-1057 retimed it to the moment the game
 * begins, roles as dealt), held on the host until game end posts it (see
 * EVENTS's `board` note for why it is not broadcast live). localStorage, so
 * the host's early view survives a mid-game reload. One entry only — a new
 * game's capture replaces the last game's orphan (an abandoned game leaves
 * nothing behind, and `take`/`peek` match by gameId so a stale stash can
 * never publish into the wrong game). First write wins per game, so a
 * re-deal press cannot re-shoot a board the game already opened on.
 */
const OPENING_KEY = "golem.boardOpening";

export function stashOpeningBoard(gameId, seats) {
  if (!gameId || !Array.isArray(seats) || !seats.length) return;
  try {
    const prior = JSON.parse(localStorage.getItem(OPENING_KEY)) || {};
    if (prior.gameId === gameId) return; // first write wins
    localStorage.setItem(OPENING_KEY, JSON.stringify({ gameId, seats }));
  } catch (e) {
    // storage denied — the game just records with an end portrait only
  }
}

/** The stashed opening ring for a game WITHOUT clearing it — the host's
 *  early view reads this every render until the end publishes it. */
export function peekOpeningBoard(gameId) {
  try {
    const stash = JSON.parse(localStorage.getItem(OPENING_KEY)) || {};
    if (stash.gameId !== gameId || !Array.isArray(stash.seats)) return null;
    return stash.seats;
  } catch (e) {
    return null;
  }
}

/** The stashed opening ring for a game, cleared on read, or null. */
export function takeOpeningBoard(gameId) {
  const seats = peekOpeningBoard(gameId);
  if (seats) {
    try {
      localStorage.removeItem(OPENING_KEY);
    } catch (e) {
      // storage denied — nothing to clear
    }
  }
  return seats;
}

export function dayOf(createdAt) {
  const at = Date.parse(createdAt);
  if (!Number.isFinite(at)) return "";
  const d = new Date(at);
  return (
    d.getDate() +
    " " +
    [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][d.getMonth()]
  );
}
